import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { partnerApi } from '../../services/api/partnerApi'
import RicMultiSelect from '../../components/partner/RicMultiSelect'
import GroupNameField from '../../components/partner/GroupNameField'
import InviteSuccessCard from '../../components/partner/InviteSuccessCard'
import type {
  PartnerCustomerLookupResponse,
  InviteCustomerResponse,
  PartnerCustomerGroupSummary,
} from '../../types/partner'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type ExistingMode = {
  exists: true
  userId: number
  firstName?: string | null
  lastName?: string | null
  groups: PartnerCustomerGroupSummary[]
}

type LookupState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'new' }
  | ({ kind: 'existing' } & ExistingMode)
  | { kind: 'error'; message: string }

function preselectFromQuery(search: URLSearchParams): number[] {
  const ric = search.get('deviceId')
  if (!ric) return []
  const n = Number(ric)
  return Number.isFinite(n) && n > 0 ? [n] : []
}

export default function CustomerInvite() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [groupName, setGroupName] = useState('')
  const [groupId, setGroupId] = useState<number | 'new' | ''>('')
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>(() =>
    preselectFromQuery(searchParams),
  )

  const [lookup, setLookup] = useState<LookupState>({ kind: 'idle' })
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [success, setSuccess] = useState<InviteCustomerResponse | null>(null)

  const isExisting = lookup.kind === 'existing'

  // Reset group dropdown when an existing customer is detected.
  useEffect(() => {
    if (isExisting) {
      const groups = (lookup as ExistingMode).groups
      setGroupId(groups.length === 1 ? groups[0].locationId : '')
    } else {
      setGroupId('')
    }
  }, [isExisting, lookup])

  const handleEmailBlur = async () => {
    const value = email.trim().toLowerCase()
    if (!EMAIL_RE.test(value)) {
      setLookup({ kind: 'idle' })
      return
    }
    setLookup({ kind: 'loading' })
    try {
      const res: PartnerCustomerLookupResponse = await partnerApi.lookupCustomer(value)
      if (res.exists && res.user) {
        setLookup({
          kind: 'existing',
          exists: true,
          userId: res.user.userId,
          firstName: res.user.firstName,
          lastName: res.user.lastName,
          groups: res.user.groups,
        })
        setFirstName(res.user.firstName || '')
        setLastName(res.user.lastName || '')
      } else {
        setLookup({ kind: 'new' })
      }
    } catch (err: any) {
      setLookup({
        kind: 'error',
        message: err?.message || 'Failed to look up email',
      })
    }
  }

  const validate = (): string | null => {
    if (!EMAIL_RE.test(email.trim())) return 'Please enter a valid email address'
    if (isExisting) {
      if (selectedDeviceIds.length === 0) return 'Select at least one RIC to add'
      const groups = (lookup as ExistingMode).groups
      if (groups.length > 1 && groupId === '') return 'Choose a group to add the RICs to'
      if (groupId === 'new' && !groupName.trim()) return 'Enter a name for the new group'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const err = validate()
    if (err) {
      setSubmitError(err)
      return
    }
    setSubmitError(null)
    setSubmitting(true)
    try {
      if (isExisting) {
        const target = lookup as ExistingMode
        const payload =
          groupId === 'new'
            ? { deviceIds: selectedDeviceIds, newGroupName: groupName.trim() }
            : groupId === ''
              ? { deviceIds: selectedDeviceIds }
              : { deviceIds: selectedDeviceIds, groupId: Number(groupId) }
        await partnerApi.addDevicesToCustomer(target.userId, payload)
        navigate(`/partner/customers/${target.userId}`)
      } else {
        const res = await partnerApi.inviteCustomer({
          email: email.trim().toLowerCase(),
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          deviceIds: selectedDeviceIds,
          groupName: groupName.trim() || undefined,
        })
        setSuccess(res)
      }
    } catch (e: any) {
      setSubmitError(e?.message || 'Failed to send invitation')
    } finally {
      setSubmitting(false)
    }
  }

  const handleInviteAnother = () => {
    setEmail('')
    setFirstName('')
    setLastName('')
    setGroupName('')
    setGroupId('')
    setSelectedDeviceIds([])
    setLookup({ kind: 'idle' })
    setSuccess(null)
    setSubmitError(null)
  }

  const heading = isExisting
    ? `Add RICs to ${(lookup as ExistingMode).firstName || ''} ${(lookup as ExistingMode).lastName || ''}`.trim() ||
      'Add RICs to existing customer'
    : 'Invite New Customer'

  const submitLabel = isExisting ? 'Add devices' : 'Send invitation'

  // Send invitation is disabled by default; it only enables when:
  //   - email is well-formed AND
  //   - at least one RIC is selected AND
  //   - the email is NOT an existing customer (existing → use "Open profile" CTA instead)
  // For an existing customer (Add Devices mode), it enables when a valid group
  // resolution is in place.
  const canSubmit = (() => {
    if (submitting) return false
    if (!EMAIL_RE.test(email.trim())) return false
    if (lookup.kind === 'loading') return false
    if (lookup.kind === 'existing') {
      // Add-Devices mode requires at least one RIC + a resolved group target.
      if (selectedDeviceIds.length === 0) return false
      const groups = (lookup as ExistingMode).groups
      if (groups.length > 1 && groupId === '') return false
      if (groupId === 'new' && !groupName.trim()) return false
      return true
    }
    if (lookup.kind === 'error') return false
    // 'idle' or 'new' — invite without devices is allowed; partner can add later.
    return true
  })()

  const existingGroups = useMemo(
    () => (isExisting ? (lookup as ExistingMode).groups : []),
    [isExisting, lookup],
  )

  if (success) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <InviteSuccessCard
          email={email}
          registrationUrl={success.registrationUrl}
          expiresAt={success.expiresAt}
          onInviteAnother={handleInviteAnother}
          onViewCustomer={() => navigate('/partner/customers')}
        />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <h1 className="text-2xl font-semibold mb-2">{heading}</h1>
      <p className="text-sm text-gray-500 mb-6">
        {isExisting
          ? 'This email already belongs to a customer. RICs you select will be added to their account.'
          : 'Enter the customer’s email and select the RICs they purchased. They’ll get a registration link by email.'}
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          <h2 className="text-lg font-medium">Customer details</h2>

          <div>
            <label className="block text-sm font-medium mb-2">Email Address *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (lookup.kind !== 'idle') setLookup({ kind: 'idle' })
              }}
              onBlur={handleEmailBlur}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="customer@example.com"
            />
            {lookup.kind === 'loading' && (
              <p className="text-xs text-gray-500 mt-1">Checking…</p>
            )}
            {lookup.kind === 'existing' && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start justify-between gap-3">
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Customer already exists</p>
                  <p className="text-xs mt-0.5">
                    {[(lookup as ExistingMode).firstName, (lookup as ExistingMode).lastName]
                      .filter(Boolean)
                      .join(' ') || email}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/partner/customers/${(lookup as ExistingMode).userId}`)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap"
                >
                  Open profile →
                </button>
              </div>
            )}
            {lookup.kind === 'error' && (
              <p className="text-xs text-red-600 mt-1">{lookup.message}</p>
            )}
          </div>

          {!isExisting && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-2">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </section>

        <section className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          {isExisting ? (
            <h2 className="text-lg font-medium">Group</h2>
          ) : (
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="w-full flex items-center justify-between text-left"
              aria-expanded={advancedOpen}
            >
              <span className="text-lg font-medium">Advanced</span>
              <span className="text-sm text-gray-500">
                {advancedOpen ? 'Hide' : 'Show'}
              </span>
            </button>
          )}

          {(isExisting || advancedOpen) && (isExisting ? (
            existingGroups.length === 0 ? (
              <GroupNameField
                value={groupName}
                onChange={setGroupName}
                label="New group name (optional)"
                hint="This customer has no groups yet. We’ll create one — leave blank to auto-name."
              />
            ) : existingGroups.length === 1 ? (
              <p className="text-sm text-gray-600">
                Adding to existing group <strong>{existingGroups[0].locationName}</strong>.
              </p>
            ) : (
              <>
                <label className="block text-sm font-medium mb-2">Add to group *</label>
                <select
                  value={groupId}
                  onChange={(e) => {
                    const v = e.target.value
                    setGroupId(v === '' ? '' : v === 'new' ? 'new' : Number(v))
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">— Select a group —</option>
                  {existingGroups.map((g) => (
                    <option key={g.locationId} value={g.locationId}>
                      {g.locationName}
                    </option>
                  ))}
                  <option value="new">+ Create a new group</option>
                </select>
                {groupId === 'new' && (
                  <div className="mt-3">
                    <GroupNameField
                      value={groupName}
                      onChange={setGroupName}
                      label="New group name *"
                      hint="Choose a name for this new site."
                    />
                  </div>
                )}
              </>
            )
          ) : (
            <GroupNameField value={groupName} onChange={setGroupName} />
          ))}
        </section>

        <section className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          <h2 className="text-lg font-medium">
            {isExisting ? 'Assign RICs *' : 'Assign RICs'}
          </h2>
          {!isExisting && (
            <p className="text-sm text-gray-500 -mt-1">
              Optional. You can also add devices later from the customer profile.
            </p>
          )}
          <RicMultiSelect
            selectedDeviceIds={selectedDeviceIds}
            onSelectionChange={setSelectedDeviceIds}
          />
        </section>

        {submitError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Working…' : submitLabel}
          </button>
        </div>
      </form>
    </div>
  )
}
